import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Shield, FolderGit2, LogIn, Activity, GraduationCap, Briefcase,
  FileText, Plus, Trash2, Download, Pencil, LogOut, ChevronRight, X, Save,
  ArrowLeft, Camera, UserCheck, CheckCircle2, TrendingUp, ChevronDown, Lock, Unlock,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/FloralBg";

const TABS = ["Overview", "Admins", "Courses", "Jobs", "Applications"];

export default function SuperAdmin() {
  const [tab, setTab] = useState("Overview");
  const { logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen">
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between border-b border-border/60 glass-strong sticky top-0 z-30">
        <div className="flex items-center gap-3"><Logo /><span className="pill text-[11px] px-3 py-1 bg-[#00D4D4]/15 text-teal-accent">SUPER ADMIN</span></div>
        <button data-testid="logout" onClick={() => { logout(); nav("/login"); }} className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300"><LogOut size={16} /> Logout</button>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="glass rounded-full p-1.5 inline-flex gap-1 mb-8 flex-wrap">
          {TABS.map((t) => (
            <button key={t} data-testid={`sa-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
                    className={`pill px-5 py-2 text-sm ${tab === t ? "bg-[#00D4D4]/15 text-teal-accent" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>
          ))}
        </div>
        {tab === "Overview" && <Overview />}
        {tab === "Admins" && <Admins />}
        {tab === "Courses" && <CoursesMgmt />}
        {tab === "Jobs" && <JobsMgmt />}
        {tab === "Applications" && <Applications />}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- shared ui */
function BackBtn({ onClick, label = "Back" }) {
  return (
    <button data-testid="back-btn" onClick={onClick}
            className="flex items-center gap-2 text-sm text-teal-accent hover:text-white mb-5 transition-colors">
      <ArrowLeft size={16} /> {label}
    </button>
  );
}

function StatCard({ icon: Icon, value, label, testid, onClick, accent }) {
  const clickable = !!onClick;
  return (
    <button disabled={!clickable} onClick={onClick} data-testid={testid}
            className={`glass ${clickable ? "glass-hover cursor-pointer" : "cursor-default"} rounded-3xl p-6 text-left w-full ${accent ? "border-[#00D4D4]/40" : ""}`}>
      <Icon size={22} className="text-teal-accent mb-4" />
      <div className="serif text-4xl font-extrabold">{value}</div>
      <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
        {label} {clickable && <ChevronRight size={13} />}
      </div>
    </button>
  );
}

function EnrollTable({ rows, status }) {
  const grid = "md:grid-cols-[2fr_1.2fr_1.1fr_1.5fr_1.5fr_1fr]";
  return (
    <div className="glass-strong rounded-2xl overflow-hidden">
      <div className={`hidden md:grid ${grid} gap-3 px-5 py-3 text-[11px] uppercase tracking-widest text-muted-foreground border-b border-border/60`}>
        <span>Mail ID</span><span>Admin</span><span>Group</span><span>Course</span><span>Start (IST)</span>
        <span>{status === "complete" ? "Completed (IST)" : "Progress"}</span>
      </div>
      <div className="divide-y divide-border/40">
        {rows.map((r, i) => (
          <div key={i} data-testid={`enroll-row-${i}`} className={`grid ${grid} gap-1 md:gap-3 px-5 py-3 text-sm`}>
            <span className="font-medium truncate">{r.email}</span>
            <span className="text-muted-foreground">{r.admin_name}</span>
            <span className="text-muted-foreground">{r.group_name}</span>
            <span className="text-muted-foreground truncate" title={r.course_title}>{r.course_title}</span>
            <span className="text-muted-foreground text-xs">{r.start_date}</span>
            {status === "complete"
              ? <span className="text-teal-accent text-xs">{r.end_date || "—"}</span>
              : <span className="text-teal-accent font-medium">{r.progress}</span>}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground px-5 py-6">No records.</p>}
      </div>
    </div>
  );
}

function mdToHtml(md = "") {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split("\n");
  let html = "", inList = false;
  const inline = (t) => esc(t)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`(.+?)`/g, "<code>$1</code>");
  for (let ln of lines) {
    if (/^### /.test(ln)) { if (inList) { html += "</ul>"; inList = false; } html += `<h3>${inline(ln.slice(4))}</h3>`; }
    else if (/^## /.test(ln)) { if (inList) { html += "</ul>"; inList = false; } html += `<h2>${inline(ln.slice(3))}</h2>`; }
    else if (/^- /.test(ln)) { if (!inList) { html += "<ul>"; inList = true; } html += `<li>${inline(ln.slice(2))}</li>`; }
    else if (ln.trim() === "") { if (inList) { html += "</ul>"; inList = false; } }
    else { if (inList) { html += "</ul>"; inList = false; } html += `<p>${inline(ln)}</p>`; }
  }
  if (inList) html += "</ul>";
  return html;
}

/* ---------------------------------------------------------------- Overview */
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/superadmin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="text-teal-accent">Loading…</div>;
  const cards = [
    { k: "total_users", label: "Users", icon: Users }, { k: "total_admins", label: "Admins", icon: Shield },
    { k: "total_projects", label: "Projects", icon: FolderGit2 }, { k: "total_logins", label: "Logins", icon: LogIn },
    { k: "active_users", label: "Active (7d)", icon: Activity }, { k: "total_enrollments", label: "Enrollments", icon: GraduationCap },
    { k: "active_enrollments", label: "Active Enrollment", icon: TrendingUp }, { k: "complete_enrollments", label: "Complete Enrollment", icon: CheckCircle2 },
    { k: "total_applications", label: "Applications", icon: FileText },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {cards.map((c) => <StatCard key={c.k} icon={c.icon} value={stats[c.k] ?? 0} label={c.label} testid={`stat-${c.k}`} />)}
    </div>
  );
}

/* ---------------------------------------------------------------- Admins */
function Admins() {
  const [screen, setScreen] = useState("list"); // list | detail | enroll | groups | groupUsers
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", max_users: 100 });
  const [detail, setDetail] = useState(null);
  const [enrollCtx, setEnrollCtx] = useState(null);
  const [enrollRows, setEnrollRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupData, setGroupData] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => api.get("/superadmin/admins").then((r) => setAdmins(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/superadmin/admins", form); toast.success("Admin created"); setForm({ email: "", password: "", name: "", max_users: 100 }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openDetail = async (id) => {
    try { const { data } = await api.get(`/superadmin/admins/${id}/detail`); setDetail(data); setScreen("detail"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openEnroll = async (status) => {
    const aid = detail.admin.id;
    const { data } = await api.get(`/superadmin/admins/${aid}/enroll-list?status=${status}`);
    setEnrollRows(data); setEnrollCtx({ status, adminName: detail.admin.name }); setScreen("enroll");
  };
  const openGroups = async () => {
    const { data } = await api.get(`/superadmin/admins/${detail.admin.id}/groups`);
    setGroups(data); setScreen("groups");
  };
  const openGroupUsers = async (g) => {
    const { data } = await api.get(`/superadmin/groups/${g.id}/users`);
    setGroupData(data); setSearch(""); setScreen("groupUsers");
  };
  const toggleLock = async (u) => {
    try {
      await api.post(`/superadmin/users/${u.id}/lock`, { locked: !u.locked });
      toast.success(!u.locked ? "User locked" : "User unlocked");
      setGroupData((gd) => ({ ...gd, users: gd.users.map((x) => x.id === u.id ? { ...x, locked: !x.locked } : x) }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (screen === "groups") return (
    <div>
      <BackBtn onClick={() => setScreen("detail")} label="Back to admin" />
      <h2 className="serif text-2xl font-bold mb-1">Groups</h2>
      <p className="text-sm text-muted-foreground mb-5">{detail.admin.name} · {groups.length} group{groups.length !== 1 ? "s" : ""}</p>
      <div className="space-y-2 max-w-2xl">
        {groups.map((g) => (
          <button key={g.id} data-testid={`sa-group-${g.id}`} onClick={() => openGroupUsers(g)}
                  className="w-full glass glass-hover rounded-xl px-5 py-4 flex items-center justify-between text-left">
            <div><div className="font-medium">{g.name}</div><div className="text-xs text-muted-foreground">{g.user_count} users</div></div>
            <ChevronRight size={16} className="text-muted-foreground" />
          </button>
        ))}
        {groups.length === 0 && <p className="text-sm text-muted-foreground">No groups under this admin.</p>}
      </div>
    </div>
  );

  if (screen === "groupUsers" && groupData) {
    const filtered = groupData.users.filter((u) => u.email.toLowerCase().includes(search.toLowerCase()));
    const grid = "md:grid-cols-[2fr_1.2fr_1.2fr_1.5fr_1.1fr]";
    return (
      <div>
        <BackBtn onClick={() => setScreen("groups")} label="Back to groups" />
        <h2 className="serif text-2xl font-bold mb-1">{groupData.group_name}</h2>
        <p className="text-sm text-muted-foreground mb-4">{groupData.admin_name} · {groupData.users.length} user{groupData.users.length !== 1 ? "s" : ""}</p>
        <input data-testid="group-user-search" value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search users by email…" className="kiq-input w-full max-w-md px-4 py-2.5 text-sm mb-5" />
        <div className="glass-strong rounded-2xl overflow-hidden">
          <div className={`hidden md:grid ${grid} gap-3 px-5 py-3 text-[11px] uppercase tracking-widest text-muted-foreground border-b border-border/60`}>
            <span>Email / User ID</span><span>Admin Name</span><span>Group Name</span><span>Start Time</span><span>Status</span>
          </div>
          <div className="divide-y divide-border/40">
            {filtered.map((u) => (
              <div key={u.id} data-testid={`group-user-${u.id}`} className={`grid ${grid} gap-1 md:gap-3 px-5 py-3 text-sm items-center`}>
                <span className="font-medium truncate">{u.email}</span>
                <span className="text-muted-foreground">{u.admin_name}</span>
                <span className="text-muted-foreground">{u.group_name}</span>
                <span className="text-muted-foreground text-xs">{u.start_time}</span>
                <button data-testid={`lock-toggle-${u.id}`} onClick={() => toggleLock(u)}
                        className={`pill px-3 py-1.5 text-xs flex items-center gap-1.5 w-fit ${u.locked ? "bg-red-500/15 text-red-400" : "bg-[#00D4D4]/15 text-teal-accent"}`}>
                  {u.locked ? <><Lock size={13} /> Locked · Unlock</> : <><Unlock size={13} /> Unlocked · Lock</>}
                </button>
              </div>
            ))}
            {filtered.length === 0 && <p className="text-sm text-muted-foreground px-5 py-6">No users found.</p>}
          </div>
        </div>
      </div>
    );
  }

  if (screen === "enroll") return (
    <div>
      <BackBtn onClick={() => setScreen("detail")} label="Back to admin" />
      <h2 className="serif text-2xl font-bold mb-1">{enrollCtx.status === "complete" ? "Complete" : "Active"} enrollments</h2>
      <p className="text-sm text-muted-foreground mb-5">{enrollCtx.adminName} · {enrollRows.length} student{enrollRows.length !== 1 ? "s" : ""}</p>
      <EnrollTable rows={enrollRows} status={enrollCtx.status} />
    </div>
  );

  if (screen === "detail" && detail) return <AdminDetail detail={detail} setDetail={setDetail} onBack={() => { setScreen("list"); load(); }} openEnroll={openEnroll} openGroups={openGroups} refresh={() => openDetail(detail.admin.id)} />;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="glass-strong rounded-3xl p-6">
        <h3 className="serif text-xl font-bold mb-4">Create admin</h3>
        <Inp label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="admin-email" />
        <Inp label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-name" />
        <Inp label="Default password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="admin-password" />
        <Inp label="Max users" type="number" value={form.max_users} onChange={(v) => setForm({ ...form, max_users: Number(v) })} testid="admin-maxusers" />
        <button data-testid="create-admin-btn" onClick={create} className="kiq-btn w-full py-2.5 text-sm mt-2 flex items-center justify-center gap-2"><Plus size={15} /> Create admin</button>
      </div>
      <div className="lg:col-span-2 glass-strong rounded-3xl p-6">
        <h3 className="serif text-xl font-bold mb-4">Admins ({admins.length})</h3>
        <div className="space-y-2">
          {admins.map((a) => (
            <button key={a.id} data-testid={`admin-row-${a.id}`} onClick={() => openDetail(a.id)}
                    className="w-full glass rounded-xl px-4 py-3 flex items-center justify-between hover:border-[#00D4D4]/40 text-left">
              <div><div className="font-medium">{a.name}</div><div className="text-xs text-muted-foreground">{a.email}</div></div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>{a.user_count}/{a.max_users} users</span><span>{a.group_count} groups</span><ChevronRight size={16} />
              </div>
            </button>
          ))}
          {admins.length === 0 && <p className="text-sm text-muted-foreground">No admins yet.</p>}
        </div>
      </div>
    </div>
  );
}

function AdminDetail({ detail, onBack, openEnroll, openGroups, refresh }) {
  const a = detail.admin;
  const [add, setAdd] = useState("");
  const [saving, setSaving] = useState(false);
  const [price, setPrice] = useState(a.course_price ?? detail.default_course_price);
  const [savingPrice, setSavingPrice] = useState(false);

  const uploadAvatar = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.put(`/superadmin/admins/${a.id}/avatar`, fd); toast.success("Profile picture updated"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const increase = async () => {
    const n = Number(add);
    if (!n) return toast.error("Enter a number of users to add");
    setSaving(true);
    try { await api.post(`/superadmin/admins/${a.id}/increase-limit`, { additional: n }); toast.success(`Limit increased by ${n}`); setAdd(""); refresh(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const savePrice = async () => {
    const p = Number(price);
    if (isNaN(p) || p < 0) return toast.error("Enter a valid price");
    setSavingPrice(true);
    try { await api.post(`/superadmin/admins/${a.id}/course-price`, { price: p }); toast.success(`Course price set to ₹${p} for ${a.name}'s users`); refresh(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingPrice(false); }
  };

  return (
    <div className="max-w-3xl">
      <BackBtn onClick={onBack} label="Back to admins" />

      {/* Profile */}
      <div className="glass-strong rounded-3xl p-7 mb-5">
        <div className="flex items-center gap-5">
          <label className="relative cursor-pointer group" data-testid="admin-avatar-upload">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-[#00D4D4]/15 flex items-center justify-center border border-[#00D4D4]/30">
              {a.avatar ? <img src={a.avatar} alt="" className="w-full h-full object-cover" />
                        : <span className="serif text-2xl text-teal-accent">{(a.name || a.email)[0].toUpperCase()}</span>}
            </div>
            <div className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <Camera size={18} className="text-white" />
            </div>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAvatar(e.target.files[0])} />
          </label>
          <div className="min-w-0">
            <h2 className="serif text-2xl font-bold truncate">{a.name}</h2>
            <div className="text-sm text-muted-foreground truncate">{a.email}</div>
            <div className="text-sm mt-1" data-testid="admin-password-plain">
              <span className="text-muted-foreground">Password: </span>
              <span className="font-mono text-teal-accent">{a.password_plain || "not stored"}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">Joined {a.created_at}</div>
          </div>
        </div>
      </div>

      {/* Limit */}
      <div className="glass-strong rounded-3xl p-7 mb-5">
        <h3 className="serif text-lg font-bold mb-4">User limit</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="glass rounded-xl p-4"><div className="serif text-3xl font-bold">{a.max_users}</div><div className="text-xs text-muted-foreground mt-1">Current limit</div></div>
          <div className="glass rounded-xl p-4"><div className="serif text-3xl font-bold text-teal-accent" data-testid="admin-remaining">{a.remaining}</div><div className="text-xs text-muted-foreground mt-1">Remaining</div></div>
        </div>
        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Increase user limit (add users)</label>
        <div className="flex gap-2">
          <input data-testid="increase-limit-input" type="number" value={add} onChange={(e) => setAdd(e.target.value)}
                 placeholder="e.g. 100" className="kiq-input flex-1 px-4 py-2.5 text-sm" />
          <button data-testid="increase-limit-btn" onClick={increase} disabled={saving} className="kiq-btn px-6 py-2.5 text-sm whitespace-nowrap">
            {saving ? "Saving…" : "Increase"}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Current {a.max_users} + entered value = new total limit.</p>
      </div>

      {/* Course Price */}
      <div className="glass-strong rounded-3xl p-7 mb-5">
        <h3 className="serif text-lg font-bold mb-1">Course Price</h3>
        <p className="text-xs text-muted-foreground mb-4">Custom course price applied to all users created by {a.name}. Default is ₹{detail.default_course_price}.</p>
        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Price (₹)</label>
        <div className="flex gap-2">
          <input data-testid="course-price-input" type="number" value={price} onChange={(e) => setPrice(e.target.value)}
                 placeholder={String(detail.default_course_price)} className="kiq-input flex-1 px-4 py-2.5 text-sm" />
          <button data-testid="course-price-btn" onClick={savePrice} disabled={savingPrice} className="kiq-btn px-6 py-2.5 text-sm whitespace-nowrap">
            {savingPrice ? "Saving…" : "Save"}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-2" data-testid="course-price-current">
          Current: {a.course_price != null ? `₹${a.course_price} (custom)` : `₹${detail.default_course_price} (default)`}
        </p>
      </div>

      {/* Groups + enrollments */}
      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard icon={FolderGit2} value={detail.group_count} label="Groups" testid="admin-group-count" onClick={openGroups} accent />
        <StatCard icon={UserCheck} value={detail.active_count} label="Active Enrollment" testid="admin-active-count" onClick={() => openEnroll("active")} accent />
        <StatCard icon={CheckCircle2} value={detail.complete_count} label="Complete Enrollment" testid="admin-complete-count" onClick={() => openEnroll("complete")} accent />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Courses */
function CoursesMgmt() {
  const [courses, setCourses] = useState([]);
  const [screen, setScreen] = useState("grid"); // grid | edit | byAdmin | enrollList | daySelect | taskEdit
  const [ctx, setCtx] = useState({});
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/courses").then((r) => setCourses(r.data));
  useEffect(() => { load(); }, []);

  const openByAdmin = async (course, status) => {
    const { data } = await api.get(`/superadmin/courses/${course.id}/enroll-summary?status=${status}`);
    setCtx({ course, status, groups: data }); setScreen("byAdmin");
  };
  const openList = async (adminEntry) => {
    const { data } = await api.get(`/superadmin/courses/${ctx.course.id}/enroll-list?status=${ctx.status}&admin_id=${adminEntry.admin_id}`);
    setCtx({ ...ctx, rows: data, adminName: adminEntry.admin_name }); setScreen("enrollList");
  };
  const saveCourse = async () => {
    try { await api.put(`/superadmin/courses/${edit.id}`, { title: edit.title, description: edit.description }); toast.success("Course updated"); setEdit(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (screen === "byAdmin") return (
    <div>
      <BackBtn onClick={() => setScreen("grid")} label="Back to courses" />
      <h2 className="serif text-2xl font-bold mb-1">{ctx.course.title}</h2>
      <p className="text-sm text-muted-foreground mb-5">{ctx.status === "complete" ? "Complete" : "Active"} enrollment · grouped by admin</p>
      <div className="space-y-2 max-w-2xl">
        {ctx.groups.map((g) => (
          <button key={g.admin_id || "none"} data-testid={`byadmin-${g.admin_id}`} onClick={() => openList(g)}
                  className="w-full glass glass-hover rounded-xl px-5 py-4 flex items-center justify-between text-left">
            <span className="font-medium">{g.admin_name}</span>
            <span className="flex items-center gap-3 text-sm text-muted-foreground"><span className="text-teal-accent font-medium">{g.count}</span> students <ChevronRight size={16} /></span>
          </button>
        ))}
        {ctx.groups.length === 0 && <p className="text-sm text-muted-foreground">No {ctx.status} enrollments.</p>}
      </div>
    </div>
  );

  if (screen === "enrollList") return (
    <div>
      <BackBtn onClick={() => setScreen("byAdmin")} label="Back to admins" />
      <h2 className="serif text-2xl font-bold mb-1">{ctx.course.title}</h2>
      <p className="text-sm text-muted-foreground mb-5">{ctx.adminName} · {ctx.status === "complete" ? "Complete" : "Active"} · {ctx.rows.length} students</p>
      <EnrollTable rows={ctx.rows} status={ctx.status} />
    </div>
  );

  if (screen === "daySelect") return <DaySelect course={ctx.course} onBack={() => setScreen("grid")}
    onContinue={(day) => { setCtx({ ...ctx, day }); setScreen("taskEdit"); }} />;

  if (screen === "taskEdit") return <TaskEditor course={ctx.course} day={ctx.day}
    onBack={() => setScreen("daySelect")} onBackToCourses={() => setScreen("grid")} />;

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {courses.map((c) => (
        <div key={c.id} className="glass glass-hover rounded-3xl p-6" data-testid={`sa-course-${c.key}`}>
          <h3 className="serif text-xl font-bold">{c.title}</h3>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button data-testid={`edit-course-${c.key}`} onClick={() => setEdit({ id: c.id, title: c.title, description: c.description })} className="text-xs glass rounded-full px-3 py-1.5 flex items-center gap-1.5"><Pencil size={12} /> Edit</button>
            <button data-testid={`edit-tasks-${c.key}`} onClick={() => { setCtx({ course: c }); setScreen("daySelect"); }} className="text-xs glass rounded-full px-3 py-1.5">Edit tasks</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <button data-testid={`active-enroll-${c.key}`} onClick={() => openByAdmin(c, "active")} className="text-xs rounded-full px-3 py-1.5 bg-[#00D4D4]/12 text-teal-accent border border-[#00D4D4]/30 hover:bg-[#00D4D4]/20 flex items-center gap-1.5"><UserCheck size={12} /> Active Enrollment</button>
            <button data-testid={`complete-enroll-${c.key}`} onClick={() => openByAdmin(c, "complete")} className="text-xs rounded-full px-3 py-1.5 bg-[#00D4D4]/12 text-teal-accent border border-[#00D4D4]/30 hover:bg-[#00D4D4]/20 flex items-center gap-1.5"><CheckCircle2 size={12} /> Complete Enrollment</button>
          </div>
        </div>
      ))}

      {edit && (
        <Modal onClose={() => setEdit(null)} title="Edit course">
          <Inp label="Title" value={edit.title} onChange={(v) => setEdit({ ...edit, title: v })} testid="edit-course-title" />
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Description</label>
          <textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={4} className="kiq-input w-full px-4 py-3 text-sm mb-4" />
          <button data-testid="save-course" onClick={saveCourse} className="kiq-btn w-full py-2.5 text-sm">Save</button>
        </Modal>
      )}
    </div>
  );
}

function DaySelect({ course, onBack, onContinue }) {
  const [day, setDay] = useState(1);
  const total = course.duration_days || 90;
  return (
    <div className="max-w-lg">
      <BackBtn onClick={onBack} label="Back to courses" />
      <div className="glass-strong rounded-3xl p-8">
        <h2 className="serif text-2xl font-bold mb-1">Edit tasks</h2>
        <p className="text-sm text-muted-foreground mb-6">{course.title} · {total} days</p>
        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Select a day</label>
        <div className="relative mb-6">
          <select data-testid="day-select" value={day} onChange={(e) => setDay(Number(e.target.value))}
                  className="kiq-input w-full px-4 py-3 text-sm appearance-none pr-10">
            {Array.from({ length: total }, (_, i) => i + 1).map((d) => <option key={d} value={d}>Day {d}</option>)}
          </select>
          <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
        <button data-testid="day-continue" onClick={() => onContinue(day)} className="kiq-btn w-full py-3 text-sm">Continue to Day {day}</button>
      </div>
    </div>
  );
}

function TaskEditor({ course, day, onBack, onBackToCourses }) {
  const [heading, setHeading] = useState("");
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/superadmin/courses/${course.id}/tasks/${day}`).then((r) => {
      if (!alive) return;
      setHeading(r.data.heading); setPages(r.data.pages || []); setLoading(false);
    }).catch((e) => { toast.error(apiErr(e)); setLoading(false); });
    return () => { alive = false; };
  }, [course.id, day]);

  const setPage = (i, patch) => setPages((ps) => ps.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const addPage = () => setPages((ps) => [...ps, { title: "New Section", content: "" }]);
  const delPage = (i) => setPages((ps) => ps.filter((_, idx) => idx !== i));
  const move = (i, dir) => setPages((ps) => {
    const j = i + dir; if (j < 0 || j >= ps.length) return ps;
    const copy = [...ps]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });
  const save = async () => {
    try { await api.put(`/superadmin/courses/${course.id}/tasks/${day}`, { heading, pages }); toast.success("Task saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <div><BackBtn onClick={onBack} /><div className="text-teal-accent">Loading task…</div></div>;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-5">
        <BackBtn onClick={onBack} label="Back to day selector" />
        <button onClick={() => setPreview((p) => !p)} data-testid="toggle-preview" className="text-xs glass rounded-full px-4 py-2 hover:border-[#00D4D4]/40">
          {preview ? "Edit mode" : "Preview (student view)"}
        </button>
      </div>
      <div className="text-xs uppercase tracking-widest text-teal-accent mb-1">{course.title} · Day {day}</div>

      {preview ? (
        <div className="glass-strong rounded-3xl p-8">
          <h1 className="serif text-3xl font-bold mb-6">{heading}</h1>
          {pages.map((p, i) => (
            <div key={i} className="mb-8">
              <div className="text-xs uppercase tracking-widest text-teal-accent mb-2">{p.title}</div>
              <div className="md" dangerouslySetInnerHTML={{ __html: mdToHtml(p.content) }} />
            </div>
          ))}
        </div>
      ) : (
        <>
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Heading</label>
          <input data-testid="task-heading" value={heading} onChange={(e) => setHeading(e.target.value)} className="kiq-input w-full px-4 py-3 text-base mb-6" />

          {pages.map((p, i) => (
            <div key={i} className="glass rounded-2xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <input data-testid={`page-title-${i}`} value={p.title} onChange={(e) => setPage(i, { title: e.target.value })}
                       className="kiq-input flex-1 px-3 py-2 text-sm font-medium" placeholder="Section title (Overview, Practice…)" />
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 rounded-lg hover:bg-white/5 disabled:opacity-30" title="Move up"><ChevronDown size={15} className="rotate-180" /></button>
                <button onClick={() => move(i, 1)} disabled={i === pages.length - 1} className="p-2 rounded-lg hover:bg-white/5 disabled:opacity-30" title="Move down"><ChevronDown size={15} /></button>
                <button data-testid={`del-page-${i}`} onClick={() => delPage(i)} className="p-2 rounded-lg text-red-400 hover:bg-red-400/10" title="Delete section"><Trash2 size={15} /></button>
              </div>
              <textarea data-testid={`page-content-${i}`} value={p.content} onChange={(e) => setPage(i, { content: e.target.value })}
                        rows={8} className="kiq-input w-full px-3 py-2 text-sm font-mono leading-relaxed" placeholder="Markdown content…" />
            </div>
          ))}

          <button data-testid="add-page" onClick={addPage} className="w-full glass rounded-2xl py-3 text-sm text-teal-accent hover:border-[#00D4D4]/40 flex items-center justify-center gap-2 mb-6">
            <Plus size={15} /> Add section / page
          </button>
        </>
      )}

      <div className="flex gap-3 sticky bottom-4">
        <button data-testid="save-task" onClick={save} className="kiq-btn flex-1 py-3 text-sm flex items-center justify-center gap-2"><Save size={16} /> Save task</button>
        <button onClick={onBackToCourses} className="glass rounded-2xl px-6 py-3 text-sm hover:border-[#00D4D4]/40">Back to courses</button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Jobs */
function JobsMgmt() {
  const [jobs, setJobs] = useState([]);
  const empty = { title: "", company: "", location: "", employment_type: "Full-time", level: "ENTRY", salary_range: "", tags: [], category: "Internship" };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/superadmin/jobs").then((r) => setJobs(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
      if (form.id) await api.put(`/superadmin/jobs/${form.id}`, payload); else await api.post("/superadmin/jobs", payload);
      toast.success("Saved"); setForm(empty); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete job?")) return; await api.delete(`/superadmin/jobs/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="glass-strong rounded-3xl p-6">
        <h3 className="serif text-xl font-bold mb-4">{form.id ? "Edit job" : "Create job"}</h3>
        <Inp label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="job-title" />
        <Inp label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="job-company" />
        <Inp label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="job-location" />
        <Inp label="Salary range" value={form.salary_range} onChange={(v) => setForm({ ...form, salary_range: v })} testid="job-salary" />
        <Inp label="Tags (comma separated)" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(v) => setForm({ ...form, tags: v })} testid="job-tags" />
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div><label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Level</label>
            <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="kiq-input w-full px-3 py-2.5 text-sm">
              {["ENTRY", "MID", "SENIOR"].map((l) => <option key={l}>{l}</option>)}</select></div>
          <div><label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="kiq-input w-full px-3 py-2.5 text-sm">
              <option>Internship</option><option>Internship + Placement</option></select></div>
        </div>
        <button data-testid="save-job" onClick={save} className="kiq-btn w-full py-2.5 text-sm">{form.id ? "Update" : "Create"} job</button>
        {form.id && <button onClick={() => setForm(empty)} className="w-full text-xs text-muted-foreground mt-2">Cancel edit</button>}
      </div>
      <div className="lg:col-span-2 space-y-3">
        {jobs.map((j) => (
          <div key={j.id} data-testid={`sa-job-${j.id}`} className="glass rounded-2xl px-5 py-4 flex items-center justify-between">
            <div><div className="font-medium">{j.title} <span className="text-xs text-teal-accent">· {j.level}</span></div><div className="text-xs text-muted-foreground">{j.company} · {j.location} · {j.category}</div></div>
            <div className="flex gap-2">
              <button onClick={() => setForm({ ...j, tags: (j.tags || []).join(", ") })} className="p-2 rounded-lg hover:bg-white/5"><Pencil size={15} /></button>
              <button data-testid={`del-job-${j.id}`} onClick={() => del(j.id)} className="p-2 rounded-lg text-red-400"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {jobs.length === 0 && <p className="text-sm text-muted-foreground">No jobs yet.</p>}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Applications */
function Applications() {
  const [apps, setApps] = useState([]);
  useEffect(() => { api.get("/superadmin/applications").then((r) => setApps(r.data)); }, []);
  return (
    <div className="glass-strong rounded-3xl p-6">
      <h3 className="serif text-xl font-bold mb-4">Job applications ({apps.length})</h3>
      <div className="space-y-2">
        {apps.map((a) => (
          <div key={a.id} className="glass rounded-xl px-4 py-3 flex items-center justify-between" data-testid={`app-${a.id}`}>
            <div><div className="font-medium">{a.user_name || a.user_email} → {a.job_title}</div><div className="text-xs text-muted-foreground">{a.company} · {a.user_email} · {new Date(a.applied_at).toLocaleDateString()}</div></div>
            <a href={`${API}/superadmin/applications/${a.id}/resume?token=${getToken()}`} className="text-xs kiq-btn px-3 py-1.5 flex items-center gap-1.5"><Download size={13} /> Resume</a>
          </div>
        ))}
        {apps.length === 0 && <p className="text-sm text-muted-foreground">No applications yet.</p>}
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", testid }) {
  return (
    <div className="mb-4">
      <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="kiq-input w-full px-4 py-2.5 text-sm" />
    </div>
  );
}
function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={onClose}>
      <div className="glass-strong rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="serif text-xl font-bold mb-4">{title}</h3>{children}
      </div>
    </div>
  );
}
